import useSeo from "@/lib/useSeo";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import ExportInquiry from "@/components/sections/ExportInquiry";
import { EXP, EXPORT_WORKFLOW, MARKETS, COMPLIANCE_NOTE } from "@/data/content";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { ArrowRight, ArrowUpRight, Clock, Package, FileCheck2, Ship, FileDown } from "lucide-react";

export default function Exports() {
  useSeo({
    title: "AquaFionix Exports – Indian Products for Global Markets",
    description: "AquaFionix exports Indian food products with Makhana (Fox Nuts) as the current focus, and shrimp & aquaculture products as an upcoming future export vertical.",
  });

  return (
    <div className="pt-20">
      <section className="relative overflow-hidden">
        <img src={EXP.exportPort} alt="Export shipping containers at port" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030D18] via-[#030D18]/85 to-[#030D18]/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">Exports</span>
          <h1 className="font-display mt-3 max-w-3xl text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            Indian Products for <span className="text-gradient-cyan">Global Markets</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
            AquaFionix connects quality Indian products with international buyers through reliable sourcing and export-oriented supply.
          </p>
        </div>
      </section>

      {/* Current vs future */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 md:grid-cols-2">
          <Reveal className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-[#071728] p-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Current Focus</span>
            <h3 className="mt-4 font-display text-2xl font-bold text-white">Makhana / Fox Nuts</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">Export-oriented supply of premium Indian Makhana for international B2B buyers — bulk, multiple grades, export packaging and private label.</p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Link to="/makhana-export" data-testid="exp-makhana-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200">Explore Makhana export <ArrowUpRight className="h-4 w-4" /></Link>
              <a href="/AquaFionix_Makhana_Fox_Nuts_Export_Catalogue.pdf" target="_blank" rel="noreferrer" download data-testid="exp-download-catalogue" className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-cyan-200"><FileDown className="h-4 w-4" /> Download Catalogue</a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#071728] p-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-300"><Clock className="h-3.5 w-3.5" /> Future Vertical</span>
            <h3 className="mt-4 font-display text-2xl font-bold text-white">RAS Shrimp & Crab</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">Planned as an upcoming export vertical. RAS-assisted, high-tech shrimp and crab farming is being developed with a zero-waste approach, subject to production, quality standards and destination-market documentation.</p>
            <p className="mt-3 text-xs text-amber-300/90 font-semibold uppercase tracking-wide">Upcoming — not currently available for export</p>
          </Reveal>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-16 lg:py-24 bg-[#04121f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="How It Works" title="Our export workflow." subtitle="A clear, buyer-first process from first inquiry to shipment." />
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
            {EXPORT_WORKFLOW.map((step, i) => (
              <div key={step} className="relative rounded-2xl border border-white/10 bg-[#071728]/80 p-4">
                <span className="font-mono-tech text-xs text-cyan-400/70">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 text-sm font-semibold text-slate-100 leading-snug">{step}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">Shipment timelines and destinations are confirmed commercially per order.</p>
        </div>
      </section>

      {/* Markets + compliance */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionHeading eyebrow="Target Markets" title="Markets we aim to serve." />
            <p className="mt-3 text-sm text-slate-400">This reflects our development focus and does not imply existing exports unless confirmed.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {MARKETS.map((m) => <span key={m} className="rounded-full border border-cyan-400/25 bg-cyan-950/30 px-4 py-2 text-sm text-cyan-100">{m}</span>)}
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {[{ i: Package, t: "Export Packaging" }, { i: FileCheck2, t: "Documentation" }, { i: Ship, t: "Shipment" }].map(({ i: Ic, t }) => (
                <div key={t} className="rounded-2xl border border-white/10 bg-[#071728]/70 p-4 text-center">
                  <Ic className="mx-auto h-5 w-5 text-cyan-300" /><p className="mt-2 text-xs text-slate-300">{t}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-white/10 bg-[#071728] p-7 h-full">
              <FileCheck2 className="h-7 w-7 text-emerald-400" />
              <h3 className="mt-4 font-display text-xl font-bold text-white">Quality & Export Compliance</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{COMPLIANCE_NOTE}</p>
              <button onClick={() => scrollToId("inquiry")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-500 px-6 py-3 text-sm font-semibold text-[#03121f] hover:-translate-y-0.5 transition-all">Start an Export Inquiry <ArrowRight className="h-4 w-4" /></button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#04121f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Export Inquiry" title="Tell us your requirement." align="center" />
          <div className="mt-10"><ExportInquiry id="inquiry" heading="Export Inquiry" /></div>
        </div>
      </section>
    </div>
  );
}
